import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as api from '../api/client';

const HUB_META = {
  davetiyeler: { label: 'Davetiye', title: 'Davetiye Ürünleri' },
  sekerlik: { label: 'Şekerlik', title: 'Şekerlik Ürünleri' },
};

function emptyProduct() {
  return {
    name: '',
    hint: '',
    image: '',
    price: '',
    priceNote: '',
    priceOnRequest: false,
    categorySlug: '',
    published: true,
  };
}

export default function ProductsPage() {
  const { hub } = useParams();
  const meta = HUB_META[hub] ?? HUB_META.davetiyeler;

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prods, cats] = await Promise.all([
        api.getProducts(hub),
        api.getCategories(hub),
      ]);
      setProducts(prods);
      setCategories(cats);
    } finally {
      setLoading(false);
    }
  }, [hub]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({
      mode: 'create',
      ...emptyProduct(),
      categorySlug: categories[0]?.slug ?? '',
    });
    setError('');
  };

  const openEdit = (item) => {
    setForm({
      mode: 'edit',
      id: item.id,
      name: item.name ?? '',
      hint: item.hint ?? '',
      image: item.image ?? '',
      price: item.price ?? '',
      priceNote: item.priceNote ?? '',
      priceOnRequest: !!item.priceOnRequest,
      categorySlug: item.categorySlug ?? '',
      published: item.published !== false,
    });
    setError('');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !form) return;
    setUploading(true);
    try {
      const media = await api.uploadMedia(file);
      setForm({ ...form, image: media.url });
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        hint: form.hint.trim(),
        image: form.image.trim(),
        priceNote: form.priceNote.trim(),
        priceOnRequest: form.priceOnRequest,
        categorySlug: form.categorySlug,
        published: form.published,
      };
      if (!form.priceOnRequest && form.price !== '') {
        payload.price = Number(form.price);
      }
      if (form.mode === 'create') {
        await api.createProduct(hub, payload);
      } else {
        await api.updateProduct(hub, form.id, payload);
      }
      setForm(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) return;
    try {
      await api.deleteProduct(hub, id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      <header className="admin-header">
        <h2>{meta.title}</h2>
        <p>Basılı {meta.label.toLowerCase()} ürünlerini ekleyin ve düzenleyin</p>
      </header>

      <div className="admin-content">
        {error && !form && <div className="admin-alert admin-alert--error">{error}</div>}

        <div className="admin-page-actions">
          <h3>Tüm ürünler ({products.length})</h3>
          <button type="button" className="admin-btn admin-btn--primary" onClick={openCreate} disabled={categories.length === 0}>
            + Ürün ekle
          </button>
        </div>

        {categories.length === 0 && (
          <div className="admin-alert admin-alert--error">
            Önce kategori eklemeniz gerekiyor.
          </div>
        )}

        {form && (
          <div className="admin-card" style={{ marginBottom: '1.25rem' }}>
            <h3 className="admin-section-title">
              {form.mode === 'create' ? 'Yeni ürün' : 'Ürün düzenle'}
            </h3>
            {error && <div className="admin-alert admin-alert--error">{error}</div>}
            <form className="admin-form admin-form--wide" onSubmit={handleSave}>
              <div className="admin-grid-2">
                <div className="admin-field">
                  <label htmlFor="prod-name">Ürün adı</label>
                  <input id="prod-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="admin-field">
                  <label htmlFor="prod-cat">Kategori</label>
                  <select id="prod-cat" value={form.categorySlug} onChange={(e) => setForm({ ...form, categorySlug: e.target.value })} required>
                    {categories.map((c) => (
                      <option key={c.id} value={c.slug}>{c.title}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="admin-field">
                <label htmlFor="prod-hint">Kısa açıklama</label>
                <input id="prod-hint" value={form.hint} onChange={(e) => setForm({ ...form, hint: e.target.value })} />
              </div>
              <div className="admin-field">
                <label htmlFor="prod-image">Görsel</label>
                <input id="prod-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="/images/products/..." />
                <input type="file" accept="image/*" onChange={handleImageUpload} style={{ marginTop: '0.5rem' }} />
                {uploading && <span className="admin-field__hint">Yükleniyor…</span>}
                {form.image && <img src={form.image} alt="" className="admin-thumb" style={{ marginTop: '0.5rem', width: 80, height: 80 }} />}
              </div>
              <div className="admin-field">
                <label>
                  <input
                    type="checkbox"
                    checked={form.priceOnRequest}
                    onChange={(e) => setForm({ ...form, priceOnRequest: e.target.checked })}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Teklif üzerine fiyat
                </label>
              </div>
              {!form.priceOnRequest && (
                <div className="admin-grid-2">
                  <div className="admin-field">
                    <label htmlFor="prod-price">Fiyat (TL)</label>
                    <input id="prod-price" type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                  </div>
                  <div className="admin-field">
                    <label htmlFor="prod-note">Fiyat notu</label>
                    <input id="prod-note" value={form.priceNote} onChange={(e) => setForm({ ...form, priceNote: e.target.value })} placeholder="100 adet için" />
                  </div>
                </div>
              )}
              <div className="admin-field">
                <label>
                  <input
                    type="checkbox"
                    checked={form.published}
                    onChange={(e) => setForm({ ...form, published: e.target.checked })}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Yayında
                </label>
              </div>
              <div className="admin-form-actions">
                <button type="submit" className="admin-btn admin-btn--primary" disabled={saving}>
                  {saving ? 'Kaydediliyor…' : 'Kaydet'}
                </button>
                <button type="button" className="admin-btn admin-btn--secondary" onClick={() => setForm(null)}>
                  İptal
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="admin-table-wrap">
          {loading ? (
            <div className="admin-empty">Yükleniyor…</div>
          ) : products.length === 0 ? (
            <div className="admin-empty">Henüz ürün yok</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Görsel</th>
                  <th>Ad</th>
                  <th>Kategori</th>
                  <th>Fiyat</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {products.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.image ? (
                        <img src={item.image} alt="" className="admin-thumb" />
                      ) : (
                        <span className="admin-badge admin-badge--muted">—</span>
                      )}
                    </td>
                    <td>{item.name}</td>
                    <td><code>{item.categorySlug}</code></td>
                    <td>
                      {item.priceOnRequest ? 'Teklif üzerine' : item.price != null ? `${item.price} ₺` : '—'}
                    </td>
                    <td>
                      <span className={`admin-badge ${item.published !== false ? 'admin-badge--ok' : 'admin-badge--muted'}`}>
                        {item.published !== false ? 'Yayında' : 'Taslak'}
                      </span>
                    </td>
                    <td>
                      <div className="admin-table__actions">
                        <button type="button" className="admin-btn admin-btn--secondary admin-btn--sm" onClick={() => openEdit(item)}>
                          Düzenle
                        </button>
                        <button type="button" className="admin-btn admin-btn--danger admin-btn--sm" onClick={() => handleDelete(item.id)}>
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
